import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Check, Loader, Gift, Crown, Sparkles, Lock 
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Pricing = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [validatedPromo, setValidatedPromo] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      checkSubscriptionStatus();
    }
  }, [isAuthenticated]);

  const checkSubscriptionStatus = async () => {
    try {
      const response = await apiClient.get('/subscription/status');
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to check subscription:', error);
    }
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;

    setLoading(true);
    try {
      // Use apiClient which will include auth token if user is logged in
      // Backend now accepts both authenticated and unauthenticated requests
      const response = await apiClient.get(
        '/subscription/validate-promo',
        { params: { promo_code: promoCode } }
      );

      setValidatedPromo(response.data);
      toast({
        title: "Promo code applied!",
        description: response.data.description,
      });
    } catch (error) {
      toast({
        title: "Invalid promo code",
        description: error.response?.data?.detail || "This promo code is not valid",
        variant: "destructive"
      });
      setValidatedPromo(null);
    }
    setLoading(false);
  };

  const handleSubscribe = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const origin_url = window.location.origin;
      
      const response = await apiClient.post('/subscription/checkout', {
        plan_id: 'premium_monthly',
        promo_code: validatedPromo?.code || null,
        origin_url
      });

      if (response.data.type === 'lifetime_free') {
        toast({
          title: "Success!",
          description: response.data.message,
        });
        navigate('/vocabulary');
      } else {
        // Redirect to Stripe
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to start checkout",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  const originalPrice = 9.99;
  const discountedPrice = validatedPromo 
    ? originalPrice * (1 - validatedPromo.discount_percent / 100)
    : originalPrice;

  const features = [
    "Extensive vocabulary list 📝",
    "Play interactive games 🎮",
    "Track your learning progress 📈",
    "Listen to authentic pronunciations 🗣️",
    "New vocab added weekly 🤩"
  ];

  if (subscriptionStatus?.is_subscribed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-red-50 py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-8">
            <Crown className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 font-heading mb-2">
              You're All Set!
            </h1>
            <p className="text-gray-600">
              You have full access to all features
            </p>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-800 font-medium">
                    {subscriptionStatus.subscription_type === 'lifetime_free' 
                      ? '🎉 Lifetime Free Access'
                      : subscriptionStatus.subscription_type === 'promo_recurring'
                      ? '🎓 Student Discount Active'
                      : '✓ Active Subscription'}
                  </p>
                  {subscriptionStatus.promo_code_used && (
                    <p className="text-xs text-green-700 mt-1">
                      Promo code: {subscriptionStatus.promo_code_used}
                    </p>
                  )}
                </div>

                <Button
                  onClick={() => navigate('/vocabulary')}
                  className="w-full bg-gradient-to-r from-green-600 to-red-600 hover:from-green-700 hover:to-red-700"
                >
                  Continue Learning
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-red-50">
      {/* Promo Banner */}
      <div className="bg-gradient-to-r from-green-600 to-red-600 text-white py-3 px-4 text-center">
        <p className="text-sm md:text-base font-semibold">
          🎉 Use code <span className="font-bold bg-white text-green-600 px-2 py-1 rounded">OPENING50</span> for 50% off your first month! 🎉
        </p>
      </div>
      
      <div className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-gradient-to-r from-green-600 to-red-600">
            <Sparkles className="w-3 h-3 mr-1" />
            Full Access
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 font-heading mb-4">
            Start Learning Today
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Subscribe to access all vocabulary, games, and features for just £9.99/month
          </p>
        </div>

        {/* Pricing Card */}
        <Card className="max-w-lg mx-auto shadow-xl border-2 border-green-100">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-gradient-to-r from-green-600 to-red-600 flex items-center justify-center">
                <Crown className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-heading">Monthly Subscription</CardTitle>
            <CardDescription>Full access to everything</CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Price */}
            <div className="text-center py-4 bg-gradient-to-r from-green-50 to-red-50 rounded-lg">
              {validatedPromo && validatedPromo.discount_percent > 0 ? (
                <>
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-2xl text-gray-400 line-through">
                      £{originalPrice.toFixed(2)}
                    </span>
                    <span className="text-4xl font-bold text-green-600">
                      £{discountedPrice.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-sm text-green-600 font-medium mt-2">
                    {validatedPromo.discount_percent}% OFF - {validatedPromo.description}
                  </p>
                </>
              ) : (
                <div className="text-4xl font-bold text-gray-900">
                  £{originalPrice.toFixed(2)}
                </div>
              )}
              <p className="text-sm text-gray-600 mt-1">per month</p>
            </div>

            {/* Promo Code */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                <Gift className="w-4 h-4 mr-2" />
                Have a promo code?
              </label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  disabled={loading || !!validatedPromo}
                />
                {!validatedPromo ? (
                  <Button
                    onClick={validatePromoCode}
                    variant="outline"
                    disabled={loading || !promoCode.trim()}
                  >
                    {loading ? <Loader className="w-4 h-4 animate-spin" /> : 'Apply'}
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      setPromoCode('');
                      setValidatedPromo(null);
                    }}
                    variant="outline"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* Features */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">What's included:</p>
              {features.map((feature, index) => (
                <div key={index} className="flex items-start">
                  <Check className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{feature}</span>
                </div>
              ))}
            </div>

            {/* Subscribe Button */}
            <Button
              onClick={handleSubscribe}
              disabled={loading}
              className="w-full h-12 text-lg bg-gradient-to-r from-green-600 to-red-600 hover:from-green-700 hover:to-red-700"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {isAuthenticated ? 'Subscribe Now' : 'Sign Up & Subscribe'}
                </>
              )}
            </Button>

            {/* Security Note */}
            <div className="flex items-center justify-center text-xs text-gray-500">
              <Lock className="w-3 h-3 mr-1" />
              Secure payment via Stripe
            </div>
          </CardContent>
        </Card>

        {/* Trust Signals */}
        <div className="mt-12 text-center text-sm text-gray-600">
          <p>✓ Cancel anytime • ✓ Secure payment • ✓ Instant access</p>
          <div className="mt-4 space-x-4">
            <a href="/terms" className="text-gray-500 hover:text-gray-700 underline">Terms of Service</a>
            <span className="text-gray-400">•</span>
            <a href="/privacy" className="text-gray-500 hover:text-gray-700 underline">Privacy Policy</a>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Pricing;
